import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { TripComparison } from '@/hooks/use-trip-comparison';
import { 
  Clock, MapPin, Route, AlertTriangle, 
  TrendingUp, TrendingDown, Timer 
} from 'lucide-react';

interface TripComparisonCardProps {
  comparison: TripComparison;
  onViewDetails?: () => void;
}

export const TripComparisonCard: React.FC<TripComparisonCardProps> = ({
  comparison,
  onViewDetails
}) => {
  const hasGpsData = !!comparison.gps_trip_id;
  
  const getAdherenceBadge = (score?: number) => {
    if (!score) return <Badge variant="outline">Sem dados</Badge>;
    if (score >= 90) return <Badge className="bg-green-500 hover:bg-green-600">Excelente</Badge>;
    if (score >= 70) return <Badge className="bg-blue-500 hover:bg-blue-600">Bom</Badge>;
    if (score >= 50) return <Badge className="bg-yellow-500 hover:bg-yellow-600">Regular</Badge>;
    return <Badge variant="destructive">Crítico</Badge>;
  };

  return (
    <Card className="hover:shadow-lg transition-shadow cursor-pointer" onClick={onViewDetails}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg">{comparison.vehicle_plate}</CardTitle>
          {getAdherenceBadge(comparison.adherence_score)}
        </div>
        <p className="text-sm text-muted-foreground">{comparison.driver_name}</p>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Origem e Destino */}
        <div className="space-y-2">
          <div className="flex items-start gap-2">
            <MapPin className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium">Origem</p>
              <p className="text-xs text-muted-foreground truncate">{comparison.planned_origin}</p>
              {hasGpsData && comparison.actual_origin_formatted && (
                <p className="text-xs text-blue-600 mt-1 truncate">
                  Real: {comparison.actual_origin_formatted}
                </p>
              )}
            </div>
          </div>
          
          <div className="flex items-start gap-2">
            <MapPin className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium">Destino</p>
              <p className="text-xs text-muted-foreground truncate">{comparison.planned_destination}</p>
              {hasGpsData && comparison.actual_destination_formatted && (
                <p className="text-xs text-blue-600 mt-1 truncate">
                  Real: {comparison.actual_destination_formatted}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Métricas Comparativas */}
        <div className="grid grid-cols-2 gap-4 pt-2 border-t">
          <div>
            <div className="flex items-center gap-1 mb-1">
              <Route className="h-3 w-3 text-muted-foreground" />
              <p className="text-xs text-muted-foreground">Distância</p>
            </div>
            <p className="text-sm font-semibold">
              {comparison.planned_distance_km.toFixed(1)} km
            </p>
            {hasGpsData && comparison.actual_distance_km && (
              <div className="flex items-center gap-1 mt-1">
                {comparison.distance_variance_km! > 0 ? (
                  <TrendingUp className="h-3 w-3 text-orange-500" />
                ) : (
                  <TrendingDown className="h-3 w-3 text-green-500" />
                )}
                <p className="text-xs text-muted-foreground">
                  {comparison.actual_distance_km.toFixed(1)} km 
                  ({comparison.distance_variance_percent! > 0 ? '+' : ''}
                  {comparison.distance_variance_percent?.toFixed(1)}%)
                </p>
              </div>
            )}
          </div>

          <div>
            <div className="flex items-center gap-1 mb-1">
              <Clock className="h-3 w-3 text-muted-foreground" />
              <p className="text-xs text-muted-foreground">Duração</p>
            </div>
            <p className="text-sm font-semibold">
              {comparison.planned_duration_min} min
            </p>
            {hasGpsData && comparison.actual_duration_min && (
              <div className="flex items-center gap-1 mt-1">
                <Timer className="h-3 w-3 text-blue-500" />
                <p className="text-xs text-muted-foreground">
                  {comparison.actual_duration_min} min
                  ({comparison.time_variance_min! > 0 ? '+' : ''}
                  {comparison.time_variance_min} min)
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Alertas de Atraso */}
        {hasGpsData && (
          <div className="pt-2 border-t space-y-1">
            {Math.abs(comparison.start_delay_min!) > 5 && (
              <div className="flex items-center gap-2 text-xs">
                <AlertTriangle className="h-3 w-3 text-yellow-500 flex-shrink-0" />
                <span>
                  Início {comparison.start_delay_min! > 0 ? 'atrasado' : 'adiantado'} 
                  em {Math.abs(comparison.start_delay_min!)} min
                </span>
              </div>
            )}
            {Math.abs(comparison.end_delay_min!) > 10 && (
              <div className="flex items-center gap-2 text-xs">
                <AlertTriangle className="h-3 w-3 text-orange-500 flex-shrink-0" />
                <span>
                  Término {comparison.end_delay_min! > 0 ? 'atrasado' : 'adiantado'} 
                  em {Math.abs(comparison.end_delay_min!)} min
                </span>
              </div>
            )}
          </div>
        )}

        {!hasGpsData && (
          <div className="pt-2 border-t">
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <AlertTriangle className="h-3 w-3" />
              <span>Viagem ainda não realizada ou não detectada</span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
